<template>
  <FormControl :title="title" :sub-title="subTitle">
    <div class="flex flex-col justify-start gap-4 w-full">
      <ChoiceGroup
        v-if="type !== 'range'"
        :type="type"
        :layout="layout"
        :fit="fit"
        :options="options"
        :shuffle="shuffle"
        :min="Number(min)"
        :max="Number(max)"
        :keyboard-settings="keyboardSettings"
        :rtl="isRtl">
        <template #item="{ item, index }">
          <ChoiceOption
            v-if="type === 'text'"
            :item="item"
            :selected="isSelected(item.id)"
            :format="format"
            :multi-select="multiSelect"
            :index="index"
            :rtl="isRtl"
            :other-title="item.editable ? otherTitle : ''"
            readonly
            :disabled="review"
            :exceeded-max-selections="model.length >= Number(props.max)"
            @edit-item-text="newValue => handleEditItemText(item.id, newValue)"
            @select-item="handleSelection(item.id, !!item.editable)"/>
          <CardChoice
            v-if="type === 'card'"
            :item="item"
            :selected="isSelected(item.id)"
            :format="format"
            readonly
            :disabled="review"
            @select-item="handleSelection(item.id, !!item.editable)"/>

          <SurveyChoice
            v-if="type === 'survey'"
            :key="item.id"
            :item="item"
            :selected="isSelected(item.id)"
            @update:selected="handleSelection(item.id, !!item.editable)"></SurveyChoice>

          <PostChoice
            v-if="type === 'post'"
            :item="item"
            :selected="isSelected(item.id)"
            @update:selected="handleSelection(item.id, !!item.editable)"></PostChoice>
        </template>
      </ChoiceGroup>
      <RangeChoiceForBlock
        v-if="type === 'range'"
        :options="options"
        :prefix="prefix"
        :suffix="suffix"
        :is-selected="isSelected"
        @handle-selection="handleSelection"/>
    </div>
  </FormControl>
</template>

<script setup lang="ts">
import {
  ChoiceGroup,
  ChoiceOption,
  CardChoice,
  PostChoice,
  SurveyChoice,
} from '@amit/components';
import { computed, ref, watch, type PropType } from 'vue';
import type {
  CardFormat,
  LayoutType,
  FitType,
  ChoiceOptionType,
  RangeOptionType,
} from '@amit/types';
import { useVModel } from '@vueuse/core';
import RangeChoiceForBlock from './RangeChoiceForBlock.vue';
import FormControl from '@/components/utils/FormControl/FormControl.vue';

const props = defineProps({
  type: {
    type: String as PropType<'text' | 'card' | 'survey' | 'post' | 'range'>,
    default: 'text',
  },
  title: {
    type: String,
    default: '',
  },
  subTitle: {
    type: String,
    default: '',
  },
  min: {
    type: String,
    default: '1',
  },
  max: {
    type: String,
    default: '4',
  },
  multiSelect: {
    type: Boolean,
    default: false,
  },
  prefix: {
    type: String,
    default: '',
  },
  suffix: {
    type: String,
    default: '',
  },
  format: {
    type: String as PropType<CardFormat>,
    default: 'button',
  },
  layout: {
    type: String as PropType<LayoutType>,
    default: 'horizontal',
  },
  fit: {
    type: String as PropType<FitType>,
    default: 'auto',
  },
  options: {
    type: Array as PropType<ChoiceOptionType[] | RangeOptionType[]>,
    default: () => [],
  },
  shuffle: {
    type: Boolean,
    default: true,
  },
  modelValue: {
    type: Array as PropType<(string | { id: string; text?: string })[]>,
    default: () => [],
  },
  rtl: {
    type: Boolean,
    default: true,
  },
  locale: {
    type: String,
    default: 'he',
  },
  keyboardSettings: {
    type: Object,
    default: () => ({
      showMathmaticalKeyboard: false,
      keyboardList: [],
    }),
  },
  review: {
    type: Boolean,
    default: false,
  },
});

const model = useVModel(props, 'modelValue');
const isRtl = computed(() => props.locale === 'he' || props.rtl);
const otherTitleRef = ref('<p></p>');

watch(
  model,
  newModelValue => {
    const item = newModelValue.find(
      item => typeof item === 'object' && 'text' in item,
    ) as { id: string; text: string };
    if (item) {
      otherTitleRef.value = item.text;
    }
  },
  { immediate: true },
);

const otherTitle = computed(() => {
  const item = model.value.find(
    item => typeof item === 'object' && 'text' in item,
  );
  if (item && typeof item === 'object' && 'text' in item && item.text) {
    return item.text;
  }
  return otherTitleRef.value;
});

const isSelected = (id: string) =>
  model.value.some(item =>
    typeof item === 'string' ? item === id : item.id === id,
  );

const handleSelection = (id: string, editable: boolean) => {
  if (props.multiSelect) {
    if (isSelected(id)) {
      model.value = model.value.filter(selectedId =>
        typeof selectedId === 'string'
          ? selectedId !== id
          : selectedId.id !== id,
      );
    } else if (model.value.length < Number(props.max)) {
      editable
        ? model.value.push({ id, text: otherTitleRef.value })
        : model.value.push(id);
    }
  } else {
    editable
      ? (model.value = [{ id, text: otherTitleRef.value }])
      : (model.value = [id]);
  }
};

const handleEditItemText = (id: string, text: string) => {
  otherTitleRef.value = text;
  model.value = model.value.map(item =>
    typeof item === 'object' && item.id === id ? { ...item, text } : item,
  );
};
</script>
